<template>
    <v-container>
        <v-row class="calendar-controller">
            <v-col
                lg="6"
                xl="5"
                class="pa-0">
                <v-select
                    v-model="currentMonth"
                    @change="changeMonth"
                    :items="monthItems"
                    :hide-details="true"
                    color="#6c6c6c"
                    outlined
                    dense
                />
            </v-col>
            <v-col
                lg="6"
                xl="4"
                offset-xl="3"
                class="controller-week">
                <div @click="changeWeek(-1)">&lt;</div>
                <div @click="changeWeek(1)">&gt;</div>
            </v-col>
        </v-row>
        <v-row class="calendar-display">
            <div
                class="calendar-week"
                v-for="day in weekDays"
                :key="genRef(day)">
                <div
                    :ref="genRef(day)"
                    :class="dayClass(genRef(day))"
                    @click="active(genRef(day))">
                    <div class="day-label">{{ dayLabels[day.index] }}</div>
                    <div class="day">{{ day.date }}</div>
                    <div class="day-events">
                        <div
                            class="day-event"
                            v-for="(event, index) in getEvent(day.index)"
                            :key="`${index}${Date.now()}`"
                            :style="`background-color: ${event}`">
                        </div>
                    </div>
                </div>
            </div>
        </v-row>
    </v-container>
</template>

<script>
export default {
    name: 'WeeklyCalendar',

    props: {
        events: {
            type: Object,
            default: () => ({})
        }
    },
    data () {
        return {
            currentDate: new Date(),
            nowDate: new Date(),
            currentMonth: new Date().getMonth(),
            activeRef: null,
            nowDateFormat: ''
        }
    },

    computed: {
        monthItems () {
            return this.$util.monthList(this.$vuetify.lang.current)
        },

        currentDay () {
            return this.currentDate.getDay()
        },

        currentDayRef () {
            return `${this.currentDate.getFullYear()}-${this.currentMonth}-${this.currentDate.getDate()}`
        },

        startWeekDate () {
            const start = new Date(this.currentDate)
            start.setDate(start.getDate() + (0 - this.currentDate.getDay()))
            return start
        },

        endWeekDate () {
            const end = new Date(this.currentDate)
            end.setDate(end.getDate() + (0 - this.currentDate.getDay()) + 6)
            return end
        },

        weekDays () {
            let count = 0
            const data = []
            const start = 0 - this.currentDate.getDay()
            const end = 0 - this.currentDate.getDay() + 7

            for (let i = start; i < end; i++) {
                const temp = new Date(this.currentDate)
                temp.setDate(temp.getDate() + i)

                data.push({
                    index: count,
                    date: temp.getDate(),
                    month: temp.getMonth(),
                    year: temp.getFullYear()
                })

                count++
            }

            return data
        },

        dayLabels () {
            return [
                this.$vuetify.lang.t('$vuetify.weeklyCalendar.sun'),
                this.$vuetify.lang.t('$vuetify.weeklyCalendar.mon'),
                this.$vuetify.lang.t('$vuetify.weeklyCalendar.tues'),
                this.$vuetify.lang.t('$vuetify.weeklyCalendar.wednes'),
                this.$vuetify.lang.t('$vuetify.weeklyCalendar.thurs'),
                this.$vuetify.lang.t('$vuetify.weeklyCalendar.fri'),
                this.$vuetify.lang.t('$vuetify.weeklyCalendar.satur')
            ]
        }
    },

    methods: {
        formatDate (date) {
            // date.getMonth() return month index which start at zero
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const day = date.getDate().toString().padStart(2, '0')

            return `${date.getFullYear()}-${month}-${day}`
        },

        loadData () {
            this.$emit('load-data', {
                startDate: this.formatDate(this.startWeekDate),
                endDate: this.formatDate(this.endWeekDate),
                currentDate: this.formatDate(this.currentDate)
            })
        },

        getData () {
            return {
                startDate: this.formatDate(this.startWeekDate),
                endDate: this.formatDate(this.endWeekDate),
                currentDate: this.formatDate(this.currentDate)
            }
        },

        changeMonth (month) {
            this.currentDate = new Date(
                `${this.currentDate.getFullYear()}-${(month + 1).toString().padStart(2, '0')}-01`
            )
            this.$nextTick(() => {
                this.active(this.currentDayRef)
            })
        },

        changeWeek (step) {
            this.currentDate = new Date(
                this.currentDate.setDate(this.currentDate.getDate() + (step * 7))
            )
            this.currentMonth = this.currentDate.getMonth()
            this.$nextTick(() => {
                this.active(this.currentDayRef)
            })
        },

        dayClass (ref) {
            let style = 'calendar-day locale-font'
            if (ref === this.activeRef) {
                style += ' active'
            }
            if (ref === this.nowDateFormat) {
                if (this.nowDateFormat !== this.activeRef) {
                    style += ' current-date'
                }
            }
            return style
        },

        active (ref, loadData = true) {
            const data = ref.split('-')
            this.currentDate = new Date(data[0], data[1], data[2])

            this.activeRef = ref
            this.loadData()
        },

        getEvent (index) {
            if (this.events[index]) {
                return this.events[index]
            }

            return []
        },

        genRef (dateObj) {
            return `${dateObj.year}-${dateObj.month}-${dateObj.date}`
        }
    },

    mounted () {
        this.active(this.currentDayRef)
        this.nowDateFormat = `${this.nowDate.getDate()}-${this.nowDate.getMonth()}`
    }
}
</script>

<style lang="scss" scoped>
::v-deep .calendar-controller {
    fieldset {
        border: none !important;
    }

    .v-select__slot {
        max-width: 8rem;
    }
}

.calendar-controller {
    .controller-week {
        display: flex;
        justify-content: flex-end;
        padding-top: 0px;
        padding-bottom: 0px;

        & > div {
            color: #6c6c6c;
            font-size: 20px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 25px;
            height: 30px;
            width: 30px;

            &:hover {
                cursor: pointer;
                color: white;
                background-color: lighten($color: #131410, $amount: 60);
            }
        }

        & > div:not(:first-child) {
            margin-left: 5px;
        }
    }
}

.calendar-display {
    display: flex;
    justify-content: space-evenly;
    min-height: 100px;
    margin-right: -20px;
    margin-left: -20px;

    .calendar-week {
        width: 14.2%;
        min-width: 36px;
        display: flex;
        justify-content: center;

        .calendar-day {
            display: flex;
            flex-flow: column;
            align-items: center;
            border-radius: 30px;
            width: 42px;
            padding: 0px 12px;

            .day-label {
                font-size: 14px;
                line-height: 1.43;
                color: #6c6c6c;
                padding: 12px 0px;
            }

            .day {
                font-size: 18px;
                line-height: 1.11;
                color: #131410;
                margin-bottom: 5px;
            }

            .day-events {
                display: flex;
                justify-content: center;

                & > div.day-event {
                    display: inline-block;
                    border-radius: 15px;
                    width: 6px;
                    height: 6px;

                    &:not(:first-child) {
                        margin-left: 4px;
                    }
                }
            }

            &.active, &.active:hover {
                cursor: default;
                pointer-events: none;
                background-color: #d76436;
                .day-label { color: #ffffff; }
                .day { color: #ffffff; }
            }

            &.current-date {
                .day-label { color: #d76436; }
                .day { color: #d76436; }
            }

            &:hover {
                cursor: pointer;
                background-color: lighten($color: #131410, $amount: 60);
            }
        }
    }
}
</style>
